import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ExpertActions } from '@techspert-io/experts';
import { ToastService } from '@techspert-io/user-alerts';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, finalize, map, startWith, tap } from 'rxjs/operators';
import {
  ExpertScreenerActionStatusMap,
  IExpertScreenerAction,
  QualtricsDataType,
} from '../../models/expertScreenerActions.models';
import {
  IScreenerMarkingResult,
  ScreenerMarkingVerdict,
} from '../../models/screenerMarking.models';
import { SurveysService } from '../../services/surveys.service';

type MarkingViewState =
  | { status: 'loading' }
  | { status: 'pending' }
  | { status: 'error' }
  | { status: 'marked'; result: IScreenerMarkingResult };

export type MarkingOverviewStatus = MarkingViewState['status'];

export interface IScreenerQuestionVm {
  id: string;
  question: string;
  answer: string;
  questionType: QualtricsDataType;
  verdict?: ScreenerMarkingVerdict;
  reasoning?: string;
}

export interface IMarkingOverviewVm {
  status: MarkingOverviewStatus;
  overallVerdict?: ScreenerMarkingVerdict;
  summary?: string[];
  total?: number;
  passCount?: number;
  holdCount?: number;
  failCount?: number;
}

export interface IScreenerResultVm {
  action: IExpertScreenerAction;
  overview: IMarkingOverviewVm;
  questions: IScreenerQuestionVm[];
}

@Component({
  selector: 'app-screener-result-dialog',
  templateUrl: './screener-result-dialog.component.html',
  styleUrls: ['./screener-result-dialog.component.scss'],
  standalone: false,
})
export class ScreenerResultDialogComponent implements OnInit {
  readonly ExpertScreenerActionStatusMap = ExpertScreenerActionStatusMap;
  isLoading = false;
  vm$: Observable<IScreenerResultVm> = EMPTY;

  screenerNotes = new FormControl<string>('');

  get expertActionId(): string {
    return this.data.expertActionId;
  }

  constructor(
    private dialogRef: MatDialogRef<
      ScreenerResultDialogComponent,
      IExpertScreenerAction
    >,
    @Inject(MAT_DIALOG_DATA)
    private data: { expertActionId: string },
    private surveysService: SurveysService,
    private toastService: ToastService,
    private store: Store
  ) {}

  ngOnInit(): void {
    const action$ = this.surveysService
      .getById(this.expertActionId)
      .pipe(tap((screener) => this.screenerNotes.patchValue(screener.notes)));

    const marking$ = this.surveysService.getMarking(this.expertActionId).pipe(
      map<IScreenerMarkingResult, MarkingViewState>((result) => ({
        status: 'marked',
        result,
      })),
      catchError<MarkingViewState, Observable<MarkingViewState>>(
        (err: HttpErrorResponse) =>
          of({
            status: err.status === 404 ? 'pending' : 'error',
          })
      ),
      startWith<MarkingViewState>({ status: 'loading' })
    );

    this.vm$ = combineLatest([action$, marking$]).pipe(
      map(([action, marking]) => this.buildViewModel(action, marking))
    );
  }

  private buildViewModel(
    action: IExpertScreenerAction,
    marking: MarkingViewState
  ): IScreenerResultVm {
    const verdictsById = new Map(
      marking.status === 'marked'
        ? marking.result.question_verdicts.map((verdict) => [
            verdict.id,
            verdict,
          ])
        : []
    );

    const questions: IScreenerQuestionVm[] = (
      action.screenerResult?.responses ?? []
    ).map((response) => {
      const mark = verdictsById.get(response.id);
      return {
        id: response.id,
        question: response.question,
        answer: this.formatAnswer(response.answer),
        questionType: response.questionType,
        verdict: mark?.verdict,
        reasoning: mark?.reasoning,
      };
    });

    return {
      action,
      overview: this.buildOverview(marking, questions.length),
      questions,
    };
  }

  private buildOverview(
    marking: MarkingViewState,
    total: number
  ): IMarkingOverviewVm {
    if (marking.status !== 'marked') {
      return { status: marking.status };
    }

    const verdicts = marking.result.question_verdicts;
    return {
      status: 'marked',
      overallVerdict: marking.result.overall_verdict,
      summary: marking.result.summary,
      total,
      passCount: verdicts.filter((v) => v.verdict === 'pass').length,
      holdCount: verdicts.filter((v) => v.verdict === 'hold').length,
      failCount: verdicts.filter((v) => v.verdict === 'failed').length,
    };
  }

  update(status?: ExpertScreenerActionStatusMap): void {
    this.isLoading = true;
    this.surveysService
      .update({
        expertActionId: this.expertActionId,
        notes: this.screenerNotes.value,
        ...(status && { status }),
      })
      .pipe(
        tap((res) => {
          if (status === ExpertScreenerActionStatusMap.CompleteFailed) {
            this.store.dispatch(
              ExpertActions.fetchExpert({ expertId: res.expertId })
            );
          }

          status &&
            this.toastService.sendMessage(
              `Screener marked ${status}`,
              'success'
            );
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe((action) => this.dialogRef.close(action));
  }

  copyQuestionsAndAnswers(questions: IScreenerQuestionVm[]): Promise<void> {
    const text = questions
      .map((q) => `Question: ${q.question}\nAnswer: ${q.answer}`)
      .join('\n\n');

    return navigator.clipboard.writeText(text).then(
      () =>
        this.toastService.sendMessage(
          'Copied questions and answers to clipboard',
          'success'
        ),
      () =>
        this.toastService.sendMessage('Failed to copy to clipboard', 'error')
    );
  }

  formatAnswer(answer: string): string {
    return (answer || '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .join(', ');
  }

  isDisabled(status?: ExpertScreenerActionStatusMap): boolean {
    return (
      this.isLoading || [ExpertScreenerActionStatusMap.Voided].includes(status)
    );
  }
}
